import type { GratuitySettingPayload } from "../../services/gratuity";

/**
 * Shapes and pure helpers shared by the desktop and mobile Gratuity tab.
 * The form keeps strings for the inputs; numbers are made at save time.
 */

export interface GratuityOptionRow {
  /** Client key for React and for the row operations below. */
  key: string;
  /** Server id when the row came from a saved setting: it is updated in place. */
  id?: string;
  valueType: number;
  value: string;
  displayName: string;
  isDefault: boolean;
}

export interface GratuityFormState {
  /** "" for the company default, else a location id. */
  locationId: string;
  enable: string;
  taxable: string;
  taxGroupId: string;
  allowCustom: string;
  customMaxPercent: string;
  cashChange: string;
  percentBase: number;
  promptMode: number;
  roundUpExact: string;
  options: GratuityOptionRow[];
}

export type GratuityYesNoKey = "enable" | "taxable" | "allowCustom" | "cashChange" | "roundUpExact";

export const GRATUITY_OPTION_TYPES = [
  { id: 1, name: "Percent" },
  { id: 2, name: "Fixed amount" },
  { id: 3, name: "Round up" },
];

export const GRATUITY_PROMPT_MODES = [
  { id: 1, name: "At payment" },
  { id: 2, name: "Card payments only" },
  { id: 3, name: "Manual only" },
];

export const GRATUITY_PERCENT_BASES = [
  { id: 1, name: "Net of tax" },
  { id: 2, name: "Total incl. tax" },
  { id: 3, name: "Total incl. tax and service charge" },
];

export const labelOf = (list: { id: number; name: string }[], id: number) =>
  list.find((item) => item.id === id)?.name ?? "";

let rowSeq = 0;

export const newGratuityOptionRow = (partial: Partial<GratuityOptionRow> = {}): GratuityOptionRow => ({
  key: `gratuity-option-${++rowSeq}`,
  valueType: 1,
  value: "",
  displayName: "",
  isDefault: false,
  ...partial,
});

/** A new setting starts with three common percent presets, all editable. */
export const emptyGratuityForm = (): GratuityFormState => ({
  locationId: "",
  enable: "yes",
  taxable: "no",
  taxGroupId: "",
  allowCustom: "yes",
  customMaxPercent: "",
  cashChange: "no",
  percentBase: 2,
  promptMode: 1,
  roundUpExact: "no",
  options: [
    newGratuityOptionRow({ value: "10" }),
    newGratuityOptionRow({ value: "15" }),
    newGratuityOptionRow({ value: "20" }),
  ],
});

export interface GratuityOptionRecord {
  id: string;
  valueType: number;
  value: number;
  displayName: string;
  position: number;
  isDefault: boolean;
}

/** Server row to the shape both layouts render; numerics arrive as strings. */
export const normalizeGratuity = (item: any) => {
  const options: GratuityOptionRecord[] = (Array.isArray(item?.options) ? item.options : [])
    .map((option: any): GratuityOptionRecord => ({
      id: option?.id ?? "",
      valueType: Number(option?.valueType ?? option?.value_type ?? 1),
      value: Number(option?.value ?? 0),
      displayName: option?.displayName ?? option?.display_name ?? "",
      position: Number(option?.position ?? 0),
      isDefault: Boolean(option?.isDefault ?? option?.is_default ?? false),
    }))
    .sort((a: GratuityOptionRecord, b: GratuityOptionRecord) => a.position - b.position);
  const customMax = item?.customMaxPercent ?? item?.custom_max_percent;
  return {
    id: item?.id ?? "",
    locationId: (item?.locationId ?? item?.location_id ?? null) as string | null,
    locationName: (item?.location?.name ?? "") as string,
    isEnabled: Boolean(item?.isEnabled ?? item?.is_enabled ?? false),
    isTaxable: Boolean(item?.isTaxable ?? item?.is_taxable ?? false),
    taxGroupId: (item?.taxGroupId ?? item?.tax_group_id ?? null) as string | null,
    taxGroupName: (item?.taxGroup?.groupName ?? "") as string,
    allowCustomGratuity: Boolean(item?.allowCustomGratuity ?? item?.allow_custom_gratuity ?? true),
    customMaxPercent: customMax === null || customMax === undefined ? null : Number(customMax),
    changeToGratuityEnabled: Boolean(item?.changeToGratuityEnabled ?? item?.change_to_gratuity_enabled ?? false),
    percentBase: Number(item?.percentBase ?? item?.percent_base ?? 2),
    promptMode: Number(item?.promptMode ?? item?.prompt_mode ?? 1),
    roundUpExactToNext: Boolean(item?.roundUpExactToNext ?? item?.round_up_exact_to_next ?? false),
    options,
  };
};

export type GratuityRecord = ReturnType<typeof normalizeGratuity>;

export const gratuityFormFromRecord = (record: GratuityRecord): GratuityFormState => ({
  locationId: record.locationId ?? "",
  enable: record.isEnabled ? "yes" : "no",
  taxable: record.isTaxable ? "yes" : "no",
  taxGroupId: record.taxGroupId ?? "",
  allowCustom: record.allowCustomGratuity ? "yes" : "no",
  customMaxPercent: record.customMaxPercent === null ? "" : String(record.customMaxPercent),
  cashChange: record.changeToGratuityEnabled ? "yes" : "no",
  percentBase: record.percentBase,
  promptMode: record.promptMode,
  roundUpExact: record.roundUpExactToNext ? "yes" : "no",
  options: record.options.map((option) =>
    newGratuityOptionRow({
      id: option.id || undefined,
      valueType: option.valueType,
      value: String(option.value),
      displayName: option.displayName ?? "",
      isDefault: option.isDefault,
    }),
  ),
});

export const gratuityPayloadFromForm = (form: GratuityFormState): GratuitySettingPayload => ({
  locationId: form.locationId || null,
  isEnabled: form.enable === "yes",
  isTaxable: form.taxable === "yes",
  taxGroupId: form.taxable === "yes" ? form.taxGroupId || null : null,
  allowCustomGratuity: form.allowCustom === "yes",
  customMaxPercent:
    form.allowCustom === "yes" && form.customMaxPercent.trim() !== "" ? Number(form.customMaxPercent) : null,
  changeToGratuityEnabled: form.cashChange === "yes",
  percentBase: form.percentBase,
  promptMode: form.promptMode,
  roundUpExactToNext: form.roundUpExact === "yes",
  options: form.options.map((row, index) => ({
    ...(row.id ? { id: row.id } : {}),
    valueType: row.valueType,
    value: Number(row.value),
    displayName: row.displayName.trim() || null,
    position: index,
    isDefault: row.isDefault,
  })),
});

/** "10% · 15% · Round up 1" for a list row; `tr` translates the type words. */
export const gratuityOptionSummary = (
  options: { valueType: number; value: number | string; displayName?: string }[],
  tr: (key: string) => string,
) => {
  if (!options.length) return tr("No options");
  return options
    .map((option) => {
      if (option.displayName) return option.displayName;
      if (option.valueType === 1) return `${option.value}%`;
      if (option.valueType === 3) return `${tr("Round up")} ${option.value}`;
      return String(option.value);
    })
    .join(" · ");
};

export const gratuityOptionValueProblem = (row: GratuityOptionRow): string | null => {
  const value = Number(row.value);
  if (row.value.trim() === "" || Number.isNaN(value)) return "Value is required";
  if (value <= 0) return "Value must be greater than 0";
  if (row.valueType === 1 && value > 100) return "A percent option cannot exceed 100";
  const cents = value * 100;
  if (Math.abs(cents - Math.round(cents)) > 1e-6) return "Option values are limited to two decimals";
  return null;
};

export const gratuityCustomMaxProblem = (form: GratuityFormState): string | null => {
  if (form.allowCustom !== "yes" || form.customMaxPercent.trim() === "") return null;
  const max = Number(form.customMaxPercent);
  if (Number.isNaN(max) || max < 0 || max > 100) return "Maximum custom % must be between 0 and 100";
  return null;
};

/** First rule the form breaks, as a locale key, or null. Mirrors the API checks. */
export const gratuityFormProblem = (form: GratuityFormState): string | null => {
  if (form.options.length === 0) return "Add at least one gratuity option";
  for (const row of form.options) {
    const problem = gratuityOptionValueProblem(row);
    if (problem) return problem;
  }
  if (form.options.filter((row) => row.isDefault).length > 1) return "Only one option can be the default";
  const customMax = gratuityCustomMaxProblem(form);
  if (customMax) return customMax;
  if (form.taxable === "yes" && !form.taxGroupId) return "Select a tax group for a taxable gratuity";
  return null;
};

export const patchGratuityOption = (options: GratuityOptionRow[], key: string, patch: Partial<GratuityOptionRow>) =>
  options.map((row) => (row.key === key ? { ...row, ...patch } : row));

export const removeGratuityOption = (options: GratuityOptionRow[], key: string) =>
  options.filter((row) => row.key !== key);

/** At most one default: marking one clears the others; unmarking clears all. */
export const setDefaultGratuityOption = (options: GratuityOptionRow[], key: string, isDefault: boolean) =>
  options.map((row) => ({ ...row, isDefault: isDefault && row.key === key }));

export const moveGratuityOption = (options: GratuityOptionRow[], key: string, delta: -1 | 1) => {
  const index = options.findIndex((row) => row.key === key);
  const target = index + delta;
  if (index < 0 || target < 0 || target >= options.length) return options;
  const next = [...options];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

/** Page numbers with "..." gaps, as the service-charge pager shows them. */
export const visiblePages = (page: number, totalPages: number): (number | string)[] => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (page <= 3) return [1, 2, 3, "...", totalPages];
  if (page >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", page - 1, page, page + 1, "...", totalPages];
};
